/**
 * LoginPage — single-form login.
 *
 * Flow:
 *   1. User submits → POST /api/auth/v2/login.
 *   2. Single membership → applyLogin auto-sets activeDepartmentId → redirect to /app/chat.
 *   3. Multiple memberships → store {token, user, memberships} but defer
 *      activeDepartmentId selection to the in-page picker rendered below.
 *
 * Visual notes:
 *   - The form uses single-rule .input fields, never a boxed input.
 *   - The submit button uses .btn-primary (ink fill) — terracotta is reserved
 *     for the masthead accent and danger states.
 *   - User-facing feedback (success / errors) goes through the global toaster
 *     via toast.success() / toast.error() — never window.alert / confirm.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, Eye, EyeOff } from "lucide-react";

import * as authService from "../../services/authService";
import { useAuthStore } from "../../stores/authStore";
import { toast } from "../../stores/toastStore";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const applyLogin            = useAuthStore((s) => s.applyLogin);
  const setActiveDepartment   = useAuthStore((s) => s.setActiveDepartment);
  const memberships           = useAuthStore((s) => s.memberships);
  const activeDepartmentId    = useAuthStore((s) => s.activeDepartmentId);
  const isAuthenticated       = useAuthStore((s) => s.isAuthenticated);

  const [email, setEmail]           = useState("");
  const [password, setPassword]     = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Honour ?next=... for deep-link redirects (set by ProtectedRoute).
  const next = new URLSearchParams(location.search).get("next") || "/app/chat";

  // If a previously-logged-in user lands on /auth/login, send them straight in.
  useEffect(() => {
    if (isAuthenticated() && activeDepartmentId != null) {
      navigate(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await authService.login(email, password);
      applyLogin(result);

      // applyLogin auto-selects the active department when there's exactly
      // one membership — straight redirect.  When there are multiple, the
      // picker below renders.  Zero memberships is a misconfiguration.
      if (result.memberships.length === 1) {
        toast.success(`Signed in as ${result.user.email}`);
        navigate(next, { replace: true });
      } else if (result.memberships.length === 0) {
        toast.error("Your account has no department memberships. Contact a sys_admin.");
      } else {
        toast.success("Signed in. Choose a department to continue.");
      }
    } catch (err) {
      toast.error(err?.data?.error || err.message || "Sign-in failed");
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Multi-membership picker ────────────────────────────────────────────
  // Only renders after a successful login when the user has more than one
  // membership and hasn't picked one yet.
  if (memberships.length > 1 && activeDepartmentId == null) {
    return (
      <div className="space-y-8">
        <div className="reveal">
          <div className="eyebrow">CHOOSE A DEPARTMENT</div>
          <h2 className="display text-[var(--text-3xl)] mt-4">
            You have access to several units.
          </h2>
          <p className="text-[var(--color-ink-2)] text-[var(--text-base)] mt-3 max-w-[36ch]">
            Pick which one to work in. You can switch at any time from the header.
          </p>
        </div>

        <ul className="border-t border-[var(--color-rule)]">
          {memberships.map((m, idx) => (
            <li
              key={m.department_id}
              className="reveal border-b border-[var(--color-rule)]"
              style={{ animationDelay: `${idx * 60}ms` }}
            >
              <button
                type="button"
                onClick={() => {
                  setActiveDepartment(m.department_id);
                  toast.success(`Working in ${m.department_name}`);
                  navigate(next, { replace: true });
                }}
                className="w-full flex items-center justify-between gap-4 py-4 group text-left"
              >
                <div>
                  <div className="text-[var(--text-lg)] font-medium">
                    {m.department_name}
                  </div>
                  <div className="mono text-[var(--text-xs)] text-[var(--color-ink-3)] uppercase tracking-[0.16em] mt-1">
                    {m.department_code} · role · {m.role}
                  </div>
                </div>
                <ChevronRight
                  size={20}
                                   className="text-[var(--color-ink-3)] group-hover:text-[var(--color-accent)] group-hover:translate-x-1 transition-all duration-200"
                />
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-10">
      <div className="reveal space-y-4">
        <div className="eyebrow">SIGN IN</div>
        <h2 className="display text-[var(--text-3xl)] leading-[1]">
          Welcome back.
        </h2>
        <p className="text-[var(--color-ink-2)] text-[var(--text-base)]">
          Use your SADT credentials to continue.
        </p>
      </div>

      <div className="space-y-7">
        <div className="reveal field" style={{ animationDelay: "120ms" }}>
          <label htmlFor="email" className="field-label">Email</label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            placeholder="admin@example.com"
          />
        </div>

        <div className="reveal field" style={{ animationDelay: "180ms" }}>
          <label htmlFor="password" className="field-label">Password</label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input pr-10"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[var(--color-ink-3)] hover:text-[var(--color-ink)] transition-colors"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
      </div>

      <div className="reveal flex items-center gap-4" style={{ animationDelay: "240ms" }}>
        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="btn btn-primary"
        >
          {submitting ? "Signing in…" : "Sign in"}
          {!submitting && <ChevronRight size={14} weight="regular" />}
        </button>
        <span className="mono text-[var(--text-xs)] text-[var(--color-ink-3)]">
          press · enter
        </span>
      </div>
    </form>
  );
}
